// material-ui
import { Grid, MenuItem, Select, Switch, Typography } from '@material-ui/core';
import { set } from 'lodash-es';
import React, { ReactElement } from 'react';

// apis
import { IBillingDetailConfiguration } from '~/src/dash/api/billing-detail/billing-detail-models';
import { IPublisher } from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
// components
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { SettingsAppBar } from '~/src/dash/components/settings-app-bar';
import { Panel } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
// utils
import { decorate } from '~/src/dash/utils/decorator';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  billingDetailConfiguration: IBillingDetailConfiguration;
  publishers: Array<Partial<IPublisher>>;
  onChange: (congifuration: IBillingDetailConfiguration) => void; // updates parent state and rerenders
  onSubmit: (congifuration: IBillingDetailConfiguration) => void; // saves/creates billing-detail-congifuration
  onClose: () => void; // navigates to parent order or line item list
}

interface IState extends IBaseState {
  // indicates an api call to update is in progress
  isUpdating: boolean;
}

export const BillingDetailConfigurationSettingsForm = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    // NOTE: Use styles when the CSS is used more than once
    styles: () => ({
      centeredSwitch: {
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '10px',
        marginRight: '40px',
      },
      formRow: {
        margin: 8,
      },
      inputLabel: {
        fontSize: '16px',
        fontWeight: 500,
        color: 'black',
        width: '170px',
      },
      itemTypography: {
        fontSize: 16,
        fontWeight: 400,
      },
      spanActive: {
        display: 'inline-flex',
        alignItems: 'center',
        width: 80,
        fontWeight: 'bold',
      },
      spanDescription: {
        display: 'inline-flex',
        alignItems: 'center',
        margin: '10px 10px',
        fontSize: 15,
      },
      titleTypography: {
        fontSize: 16,
        fontWeight: 500,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      isUpdating: false,
      snackbarError: false,
      snackbarMessage: '',
    };

    // -------- FORM UPDATE FUNCTIONS -------- //

    // a generic form handler for form input elements.
    handleFormChange = (elem: React.ChangeEvent<HTMLInputElement>): void => {
      const { type, name: path, value, checked } = elem.target;
      let parsedValue;
      switch (type) {
        // for input[type=number] fields
        case 'number':
          parsedValue = Number(value);
          break;
        // <Checkbox/> returned a boolean on the checked property of the target
        case 'checkbox':
          parsedValue = checked;
          break;
        // <RadioGroup/> return the value of the <Radio> element
        case 'radio':
          parsedValue = path === 'AudienceTargetingOperationType' ? parseInt(value, 10) : value;
          break;
        // Everything else including <input> fields return strings
        default:
          parsedValue = value;
      }

      // Create a new order so that we can bubble up change
      const newBillingDetailConfiguration = { ...this.props.billingDetailConfiguration };

      // path can be a top level deal property like "Name" for Order.Name or go deeper such as
      // "SspParams.PackageID" for Deal.SspParams.PackageID
      set<IBillingDetailConfiguration>(newBillingDetailConfiguration, path, parsedValue);

      // bubble up event
      this.props.onChange(newBillingDetailConfiguration);
    };

    genPublisherRenderString = (pbID: string) => {
      const pb = this.props.publishers.filter(
        (publisher: Partial<IPublisher>) => pbID === publisher.ID,
      );

      if (pb.length > 0) {
        return `[${pb[0].ID}] ${pb[0].Name}`;
      }
      return '';
    };

    // -------- RENDER HELPER FUNCTIONS -------- //
    // NOTE: We destructure props and states used in render functions to clearly
    // show what we are passing as well as keeping the code clean and easier to read

    // renders top bar => name edit, save, close, delete
    renderTopBar = (): ReactElement => {
      const { isUpdating } = this.state;
      const { billingDetailConfiguration, onSubmit, onClose } = this.props;

      return (
        <Grid item>
          <SettingsAppBar
            hasWriteRights={true}
            isUpdating={isUpdating}
            handleSave={() => onSubmit(billingDetailConfiguration)}
            handleClose={onClose}
            relative
          />
        </Grid>
      );
    };

    renderDetails = (): ReactElement => {
      const { classes, billingDetailConfiguration, onChange, publishers } = this.props;

      return (
        <Grid
          item
          style={{
            width: '100%',
            marginTop: 15,
            marginLeft: 20,
            height: '100%',
            paddingBottom: 50,
          }}
        >
          <Grid container spacing={2} direction='column'>
            {/* Publisher */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Publisher</Typography>
              </Grid>
              <Grid item xs={8}>
                <Select
                  key='PublisherID'
                  fullWidth
                  label='Publisher'
                  name='PublisherID'
                  style={{ width: 200 }}
                  value={billingDetailConfiguration.PublisherID}
                  renderValue={(selected: any) => this.genPublisherRenderString(selected)}
                  onChange={this.handleFormChange}
                >
                  {publishers.map((publisher: Partial<IPublisher>): ReactElement => {
                    return (
                      <MenuItem key={publisher.ID} value={publisher.ID} style={{ width: '100%' }}>
                        {`[${publisher.ID}] ${publisher.Name}`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </Grid>
            </Grid>

            {/* Publisher Name Alias */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Publisher Name Alias</Typography>
              </Grid>
              <Grid item xs={8}>
                <FocusedTextfield
                  key='PublisherNameAlias'
                  fullWidth
                  style={{ width: '60%' }}
                  label=''
                  name='PublisherNameAlias'
                  value={billingDetailConfiguration.PublisherNameAlias}
                  onChange={this.handleFormChange}
                />
              </Grid>
            </Grid>

            {/* Product */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Product</Typography>
              </Grid>
              <Grid item xs={8}>
                <Select
                  key='Product'
                  fullWidth
                  label='Product'
                  name='Product'
                  style={{ width: 200 }}
                  value={billingDetailConfiguration.Product}
                  onChange={this.handleFormChange}
                >
                  {['Ad Platform', 'SSAI', 'Audience Manager'].map(
                    (product: string): ReactElement => {
                      return (
                        <MenuItem key={product} value={product} style={{ width: '100%' }}>
                          {product}
                        </MenuItem>
                      );
                    },
                  )}
                </Select>
              </Grid>
            </Grid>

            {/* IsTiered */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Is Tiered?</Typography>
              </Grid>
              <Grid item xs={8}>
                {EBool.toBool(billingDetailConfiguration.IsTiered) ? 'Is Tiered' : 'Is Not Tiered'}
                <Switch
                  name='IsTiered'
                  key='IsTiered'
                  checked={EBool.toBool(billingDetailConfiguration.IsTiered)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newBillingDetailConfiguration = {
                      ...this.props.billingDetailConfiguration,
                    };
                    newBillingDetailConfiguration.IsTiered = EBool.fromBool(event.target.checked);
                    onChange(newBillingDetailConfiguration);
                  }}
                />
              </Grid>
            </Grid>

            {/* Min Price */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Min Price</Typography>
              </Grid>
              <Grid item xs={8}>
                {/* make a input combination for min price*/}
                <Grid container>
                  <Grid item xs={4}>
                    {EBool.toBool(billingDetailConfiguration.HasMinPrice)
                      ? 'Has Min Price'
                      : 'No Min Price'}
                    <Switch
                      name='HasMinPrice'
                      key='HasMinPrice'
                      checked={EBool.toBool(billingDetailConfiguration.HasMinPrice)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newBillingDetailConfiguration = {
                          ...this.props.billingDetailConfiguration,
                        };
                        newBillingDetailConfiguration.HasMinPrice = EBool.fromBool(
                          event.target.checked,
                        );
                        onChange(newBillingDetailConfiguration);
                      }}
                    />
                  </Grid>
                  {billingDetailConfiguration.HasMinPrice === EBool.EBool_TRUE && (
                    <Grid item xs={4}>
                      <FocusedTextfield
                        style={{ paddingTop: 10 }}
                        key='MinPrice'
                        fullWidth
                        label=''
                        type='number'
                        name='MinPrice'
                        value={billingDetailConfiguration.MinPrice}
                        onChange={this.handleFormChange}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Custom Campaign Price */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Custom Campaign Price</Typography>
              </Grid>
              <Grid item xs={8}>
                {/* make a input combination for Custom Campaign Price*/}
                <Grid container>
                  <Grid item xs={4}>
                    {EBool.toBool(billingDetailConfiguration.HasCustomCampaignPrice)
                      ? 'Has Campaign Price'
                      : 'No Campaign Price'}
                    <Switch
                      name='HasCustomCampaignPrice'
                      key='HasCustomCampaignPrice'
                      checked={EBool.toBool(billingDetailConfiguration.HasCustomCampaignPrice)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newBillingDetailConfiguration = {
                          ...this.props.billingDetailConfiguration,
                        };
                        newBillingDetailConfiguration.HasCustomCampaignPrice = EBool.fromBool(
                          event.target.checked,
                        );
                        onChange(newBillingDetailConfiguration);
                      }}
                    />
                  </Grid>
                  {billingDetailConfiguration.HasCustomCampaignPrice === EBool.EBool_TRUE && (
                    <Grid item xs={4}>
                      <FocusedTextfield
                        style={{ paddingTop: 10 }}
                        key='CustomCampaignPrice'
                        fullWidth
                        label=''
                        type='number'
                        name='CustomCampaignPrice'
                        value={billingDetailConfiguration.CustomCampaignPrice}
                        onChange={this.handleFormChange}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>

            {/* Custom Vast Price */}
            <Grid container alignItems='center' spacing={2} className={classes.formRow}>
              <Grid item xs={2}>
                <Typography className={classes.itemTypography}>Custom Vast Price</Typography>
              </Grid>
              <Grid item xs={8}>
                {/* make a input combination for Custom Vast Price*/}
                <Grid container>
                  <Grid item xs={4}>
                    {EBool.toBool(billingDetailConfiguration.HasCustomVastPrice)
                      ? 'Has Vast Price'
                      : 'No Vast Price'}
                    <Switch
                      name='HasCustomVastPrice'
                      key='HasCustomVastPrice'
                      checked={EBool.toBool(billingDetailConfiguration.HasCustomVastPrice)}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newBillingDetailConfiguration = {
                          ...this.props.billingDetailConfiguration,
                        };
                        newBillingDetailConfiguration.HasCustomVastPrice = EBool.fromBool(
                          event.target.checked,
                        );
                        onChange(newBillingDetailConfiguration);
                      }}
                    />
                  </Grid>
                  {billingDetailConfiguration.HasCustomVastPrice === EBool.EBool_TRUE && (
                    <Grid item xs={4}>
                      <FocusedTextfield
                        style={{ paddingTop: 10 }}
                        key='CustomVastPrice'
                        fullWidth
                        label=''
                        type='number'
                        name='CustomVastPrice'
                        value={billingDetailConfiguration.CustomVastPrice}
                        onChange={this.handleFormChange}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    render() {
      return (
        <>
          <Panel>{this.renderTopBar()}</Panel>
          <Panel scroll largePadding>
            {this.renderDetails()}
          </Panel>
        </>
      );
    }
  },
);
